// src/Dashboard/pages/user/Galleries/components/PhotoDrawer.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaEye,
  FaEyeSlash,
  FaImage,
  FaSave,
  FaTimes,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  addPhoto,
  deletePhoto,
  updatePhoto,
} from "../../../../../services/photoServices";

import {
  replaceAlbumPhoto,
  toAbsolutePhotoUrl as toAbsPhoto,
} from "../../../../../services/albumPhotoServices";

import { getGalleryById } from "../../../../../services/gallerieServices";

import {
  FALLBACK_IMAGE,
  normalizePlainText,
  onImgError,
} from "../utils/galleriesUtils";

export default function PhotoDrawer({
  isOpen,
  mode = "create",
  galleryId,
  photo,
  onClose,
  onSaved,
}) {
  const isEdit = mode === "edit" && !!photo?.id;

  const fileInputRef = useRef(null);
  const firstFieldRef = useRef(null);

  const [currentId, setCurrentId] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [position, setPosition] = useState("");
  const [isCover, setIsCover] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setCurrentId(isEdit ? photo.id : null);
    setPhotoUrl(isEdit ? photo?.photo_url || "" : "");
    setDescription(isEdit ? photo?.description || "" : "");
    setIsActive(isEdit ? photo?.is_active !== false : true);
    setPosition(isEdit && photo?.position ? String(photo.position) : "");
    setIsCover(isEdit ? !!photo?.is_cover : false);
    setSelectedFile(null);
    setSaving(false);
    setUploading(false);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const timer = window.setTimeout(() => {
      firstFieldRef.current?.focus();
    }, 80);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEdit, photo]);

  useEffect(() => {
    if (!isOpen || isEdit || !galleryId) return;

    (async () => {
      try {
        setLoadingDefaults(true);

        const gallery = await getGalleryById(Number(galleryId));
        const photos = Array.isArray(gallery?.photos) ? gallery.photos : [];

        const maxPosition = photos.reduce((max, item) => {
          const pos = Number(item?.position);

          return Number.isFinite(pos) ? Math.max(max, pos) : max;
        }, 0);

        setPosition(String(maxPosition + 1));
      } catch {
        setPosition("");
      } finally {
        setLoadingDefaults(false);
      }
    })();
  }, [isOpen, isEdit, galleryId]);

  if (!isOpen) return null;

  const previewUrl = selectedFile
    ? URL.createObjectURL(selectedFile)
    : toAbsPhoto(photoUrl) || "";

  const buildPayload = (forCreate = false) => {
    const payload = {
      gallerie_id: galleryId,
      description: normalizePlainText(description),
      is_active: !!isActive,
    };

    if (
      String(position).trim() !== "" &&
      !Number.isNaN(parseInt(position, 10))
    ) {
      payload.position = parseInt(position, 10);
    }

    if (forCreate) {
      payload.is_cover = !!isCover;
    }

    return payload;
  };

  const handleFile = (file) => {
    if (file && file.type.startsWith("image/")) {
      setSelectedFile(file);
      return;
    }

    toast.warn("Choisissez une image valide.");
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer.files?.[0];

    handleFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleUploadImage = async () => {
    if (!selectedFile) {
      toast.warn("Choisissez une image avant de téléverser.");
      return;
    }

    try {
      setUploading(true);

      if (!currentId) {
        const created = await addPhoto(buildPayload(true));
        const createdId = created?.id || null;

        setCurrentId(createdId);
        setPhotoUrl(created?.photo_url || "");

        if (createdId) {
          const updated = await replaceAlbumPhoto(
            galleryId,
            createdId,
            selectedFile,
            { format: "webp" }
          );

          setPhotoUrl(updated?.photo_url || created?.photo_url || "");
        }

        setSelectedFile(null);
        toast.success("Image téléversée sur le serveur.");
        return;
      }

      const updated = await replaceAlbumPhoto(galleryId, currentId, selectedFile, {
        format: "webp",
      });

      setPhotoUrl(updated?.photo_url || "");
      setSelectedFile(null);
      toast.success("Image remplacée sur le serveur.");
    } catch (err) {
      console.error("Erreur upload photo :", err);
      toast.error("Échec du téléversement de l’image.");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveInfos = async () => {
    try {
      setSaving(true);

      if (!currentId) {
        const created = await addPhoto(buildPayload(true));

        setCurrentId(created?.id || null);
        setPhotoUrl(created?.photo_url || "");

        toast.success("Infos enregistrées. Vous pouvez téléverser l’image.");
        return;
      }

      await updatePhoto(currentId, buildPayload(false));
      toast.success("Infos mises à jour.");
    } catch (err) {
      console.error("Erreur sauvegarde photo :", err);
      toast.error("Échec de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = async () => {
    await onSaved?.();
  };

  const handleClose = async () => {
    const noImage = !photoUrl && !selectedFile;
    const noDesc = !description.trim();

    if (currentId && noImage && noDesc && !isEdit) {
      const ok = window.confirm("Annuler et supprimer l’entrée vide ?");

      if (ok) {
        try {
          await deletePhoto(currentId);
          toast.success("Photo supprimée.");
        } catch {
          toast.error("Échec de la suppression.");
        }
      }
    }

    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              <FaImage />
              Photo d’album
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              {isEdit ? "Modifier la photo" : "Ajouter une photo"}
            </h2>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FaTimes />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="space-y-4">
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="overflow-hidden rounded-[24px] border border-dashed border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-950"
              >
                <div
                  className="w-full bg-slate-100 dark:bg-slate-900"
                  style={{ aspectRatio: "16 / 9" }}
                >
                  <img
                    src={previewUrl || FALLBACK_IMAGE}
                    alt="Aperçu"
                    className="h-full w-full object-contain"
                    onError={onImgError}
                  />
                </div>

                <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-slate-600 dark:text-slate-300">
                    Déposez une image ou{" "}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="font-bold text-[#013E8E] underline dark:text-indigo-300"
                    >
                      sélectionnez un fichier
                    </button>
                    .
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) =>
                        handleFile(event.target.files?.[0] || null)
                      }
                    />

                    <button
                      type="button"
                      onClick={handleUploadImage}
                      disabled={uploading || !selectedFile}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <FaUpload />
                      {uploading ? "Téléversement..." : "Téléverser"}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  URL de l’image serveur
                </label>

                <input
                  ref={firstFieldRef}
                  type="text"
                  value={toAbsPhoto(photoUrl)}
                  readOnly
                  placeholder="Visible après téléversement"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Légende / description
                </label>

                <textarea
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={4}
                  placeholder="Ex. Podium Minimes — Merci aux bénévoles !"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Position dans l’album
                </label>

                <input
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={position}
                  onChange={(event) => setPosition(event.target.value)}
                  placeholder={
                    loadingDefaults
                      ? "Chargement..."
                      : "Laisser vide = position suivante"
                  }
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />

                <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Laissez vide pour laisser le système choisir la prochaine
                  position.
                </p>
              </div>

              {!isEdit && (
                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                  <label className="mb-3 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Couverture
                  </label>

                  <button
                    type="button"
                    onClick={() => setIsCover((value) => !value)}
                    className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-bold ring-1 transition ${
                      isCover
                        ? "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20"
                        : "bg-white text-slate-700 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
                    }`}
                  >
                    {isCover ? "Photo de couverture" : "Définir en couverture"}
                  </button>
                </div>
              )}

              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <label className="mb-3 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Visibilité
                </label>

                <button
                  type="button"
                  onClick={() => setIsActive((value) => !value)}
                  className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-bold ring-1 transition ${
                    isActive
                      ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                      : "bg-slate-200 text-slate-700 ring-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700"
                  }`}
                >
                  {isActive ? <FaEye /> : <FaEyeSlash />}
                  {isActive ? "Photo visible" : "Photo masquée"}
                </button>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Astuce : vous pouvez enregistrer les informations puis
                  téléverser l’image. Le bouton “Terminer” ferme le drawer et
                  recharge l’album.
                </p>
              </div>
            </div>
          </div>
        </div>

        <footer className="border-t border-slate-200 bg-white/95 p-4 dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleSaveInfos}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />
              {saving ? "Enregistrement..." : "Enregistrer les infos"}
            </button>

            <button
              type="button"
              onClick={handleFinish}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Terminer
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}